import { count } from './shared'
import styles from './runsTable.module.scss'

export type BureauCoverageTone = 'ok' | 'warn' | 'muted'

export const bureauCoveragePercent = (
    bureauCount: number | null | undefined,
    completeCount: number | null | undefined
) => {
    const bureau = count(bureauCount)
    const complete = count(completeCount)
    if (complete <= 0) return null
    // One decimal place — enough to show small gaps without rounding to 100%/0%.
    return Math.round((bureau / complete) * 1000) / 10
}

export const formatCoveragePercentLabel = (percent: number | null) => {
    if (percent == null) return '—'
    return Number.isInteger(percent) ? `${percent}%` : `${percent.toFixed(1)}%`
}

export const bureauCoverageTone = (percent: number | null): BureauCoverageTone => {
    if (percent == null) return 'muted'
    if (percent >= 100) return 'ok'
    return 'warn'
}

export const bureauCoverageCountLabel = (
    bureauCount: number | null | undefined,
    completeCount: number | null | undefined
) => {
    const bureau = count(bureauCount)
    const complete = count(completeCount)
    if (complete <= 0 && bureau <= 0) return 'No searches yet'
    return `${bureau.toLocaleString('en-GB')} of ${Math.max(complete, bureau).toLocaleString('en-GB')}`
}

interface BureauCoverageRingProps {
    label: string
    bureauCount: number | null | undefined
    completeCount: number | null | undefined
    /**
     * `stat` — dial only, for StatCard value slots.
     * `inline` — compact dial + label, for history Results.
     */
    variant?: 'stat' | 'inline'
}

const SIZE = {
    stat: { box: 56, radius: 21, stroke: 5, font: 12 },
    inline: { box: 44, radius: 16, stroke: 4, font: 10 },
} as const

/**
 * Circular coverage indicator for Equifax / TransUnion share of completed searches.
 */
export const BureauCoverageRing = ({
    label,
    bureauCount,
    completeCount,
    variant = 'stat',
}: BureauCoverageRingProps) => {
    const percent = bureauCoveragePercent(bureauCount, completeCount)
    const tone = bureauCoverageTone(percent)
    const dims = SIZE[variant]
    const circumference = 2 * Math.PI * dims.radius
    const progress = percent == null ? 0 : Math.min(100, Math.max(0, percent)) / 100
    const dash = circumference * progress
    const centreLabel = formatCoveragePercentLabel(percent)
    const countLabel = bureauCoverageCountLabel(bureauCount, completeCount)

    return (
        <div
            className={`${styles.bureauRing} ${styles[`bureauRing_${tone}`]} ${
                variant === 'inline' ? styles.bureauRingInline : styles.bureauRingStat
            }`}
            title={`${label}: ${countLabel}`}
            aria-label={`${label} ${centreLabel}, ${countLabel}`}
        >
            <span className={styles.bureauRingDial}>
                <svg
                    className={styles.bureauRingSvg}
                    width={dims.box}
                    height={dims.box}
                    viewBox={`0 0 ${dims.box} ${dims.box}`}
                    aria-hidden
                >
                    <circle
                        className={styles.bureauRingTrack}
                        cx={dims.box / 2}
                        cy={dims.box / 2}
                        r={dims.radius}
                        fill="none"
                        strokeWidth={dims.stroke}
                    />
                    <circle
                        className={styles.bureauRingProgress}
                        cx={dims.box / 2}
                        cy={dims.box / 2}
                        r={dims.radius}
                        fill="none"
                        strokeWidth={dims.stroke}
                        strokeLinecap="round"
                        strokeDasharray={`${dash} ${circumference}`}
                        transform={`rotate(-90 ${dims.box / 2} ${dims.box / 2})`}
                    />
                </svg>
                <span className={styles.bureauRingPercent} style={{ fontSize: dims.font }}>
                    {centreLabel}
                </span>
            </span>
            {variant === 'inline' ? <span className={styles.bureauRingInlineLabel}>{label}</span> : null}
        </div>
    )
}
